import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import {
  Card,
  Table,
  Select,
  DatePicker,
  Row,
  Col,
  Typography,
  Space,
  Button,
  Result,
  Spin,
  Statistic,
  Tooltip,
  message,
  Avatar,
  Tag,
  Tabs,
  Modal,
  Form,
  Input,
  Popconfirm,
} from 'antd';
import {
  ClockCircleOutlined,
  FileTextOutlined,
  DownloadOutlined,
  BarChartOutlined,
  TrophyOutlined,
  ReloadOutlined,
  ClearOutlined,
  UnorderedListOutlined,
  TeamOutlined,
  EditOutlined,
  StopOutlined,
  DollarOutlined,
} from '@ant-design/icons';
import moment from 'moment-timezone';
import { handleApiError, handleApiSuccess, accessControls } from 'utils';
import {
  getProjectColor,
  getNoProjectColor,
} from 'utils/time_tracker_project_color';
import {
  timeTrackerProjectsListService,
  timeTrackerReportsEntriesService,
  timeTrackerReportsSummaryService,
  timeTrackerReportsTeamActivityService,
  timeTrackerReportsPayrollService,
  timeTrackerEntryUpdateService,
  timeTrackerEntryStopService,
} from 'services/time_tracker';
import { usersListService, usersVerifyTokenService } from 'services/user';
import Auth from 'auth';
import {
  loadStoredTimezone,
  getEffectiveTimezone,
} from 'pages/time_tracker/tracker/tracker_timezone';
import 'pages/time_tracker/reports/reports.css';

const { RangePicker } = DatePicker;
const { TabPane } = Tabs;
const { Text } = Typography;

const DATE_PRESETS = [
  { value: 'thisWeek', label: 'This week (Mon–Sun)' },
  { value: 'lastWeek', label: 'Last week (Mon–Sun)' },
  { value: 'last7', label: 'Last 7 days' },
  { value: 'last15', label: 'Last 15 days' },
  { value: 'thisMonth', label: 'This month' },
  { value: 'lastMonth', label: 'Last month' },
  { value: 'custom', label: 'Custom range' },
];

/**
 * Returns the Monday that anchors the work week for `refMoment`, at 00:01
 * local to `refMoment`'s timezone. The weekly reports start at Monday 12:01 AM
 * (per product spec) and end Sunday 23:59:59.999.
 */
function getWorkWeekStart(refMoment) {
  const m = refMoment.clone();
  const dow = m.day(); // 0=Sun … 6=Sat
  const daysFromMonday = dow === 0 ? 6 : dow - 1;
  return m
    .clone()
    .startOf('day')
    .subtract(daysFromMonday, 'days')
    .hour(0)
    .minute(1)
    .second(0)
    .millisecond(0);
}

function getDateRange(preset, customRange, timeZone) {
  const tz = timeZone || moment.tz.guess();
  const now = moment.tz(tz);
  let from;
  let to;
  switch (preset) {
    case 'thisWeek': {
      from = getWorkWeekStart(now);
      to = from.clone().add(6, 'days').endOf('day');
      break;
    }
    case 'lastWeek': {
      from = getWorkWeekStart(now).subtract(7, 'days');
      to = from.clone().add(6, 'days').endOf('day');
      break;
    }
    case 'last7':
      from = now.clone().subtract(7, 'days').startOf('day');
      to = now.clone().endOf('day');
      break;
    case 'last15':
      from = now.clone().subtract(15, 'days').startOf('day');
      to = now.clone().endOf('day');
      break;
    case 'thisMonth':
      from = now.clone().startOf('month');
      to = now.clone().endOf('day');
      break;
    case 'lastMonth':
      from = now.clone().subtract(1, 'month').startOf('month');
      to = now.clone().subtract(1, 'month').endOf('month');
      break;
    case 'custom':
      if (customRange && customRange[0] && customRange[1]) {
        from = moment
          .tz(customRange[0].format('YYYY-MM-DD'), tz)
          .startOf('day');
        to = moment.tz(customRange[1].format('YYYY-MM-DD'), tz).endOf('day');
      }
      break;
    default:
      from = now.clone().subtract(7, 'days').startOf('day');
      to = now.clone().endOf('day');
  }
  return { from: from?.toISOString(), to: to?.toISOString() };
}

/**
 * Round to nearest 15 minutes (half-up at 7m 30s). Returns seconds.
 * 0–7:29 → 0; 7:30–22:29 → 15m; 22:30–37:29 → 30m; 37:30–52:29 → 45m; etc.
 */
function roundDuration(seconds) {
  if (seconds == null || !Number.isFinite(seconds)) return 0;
  const s = Math.floor(Number(seconds));
  if (s <= 0) return 0;
  return Math.round(s / 900) * 900;
}

/**
 * Aggregate Team activity footer: sum stopped seconds per listed row + merged
 * project breakdown for stacked bar.
 */
function computeTeamActivityRollup(teamRows) {
  let sumRaw = 0;
  let sumRounded = 0;
  const projectAgg = new Map();

  (teamRows || []).forEach((row) => {
    const sec = row.weekTotalSeconds || 0;
    sumRaw += sec;
    sumRounded += roundDuration(sec);

    (row.weekByProject || []).forEach((seg) => {
      const pid = seg.project?._id != null ? String(seg.project._id) : 'none';
      const prev = projectAgg.get(pid) || {
        project: seg.project || null,
        seconds: 0,
      };
      prev.seconds += seg.seconds || 0;
      if (!prev.project && seg.project) prev.project = seg.project;
      projectAgg.set(pid, prev);
    });
  });

  const weekByProject = [...projectAgg.values()]
    .map((v) => ({
      project: v.project,
      seconds: Math.round(v.seconds || 0),
    }))
    .filter((x) => x.seconds > 0)
    .sort((a, b) => b.seconds - a.seconds);

  return { sumRaw, sumRounded, weekByProject };
}

/** Convert seconds to decimal hours for export (15 min=.25, 30 min=.5, 1 hr=1). */
function secondsToDecimalHours(seconds) {
  if (seconds == null || !Number.isFinite(seconds)) return '0';
  const h = Number(seconds) / 3600;
  return h % 1 === 0 ? String(h) : h.toFixed(2);
}

/**
 * Show a rounded duration as hours + minutes after snapping to the nearest
 * 15-minute block (half-up at the 7m 30s midpoint between intervals).
 */
function formatRoundedDuration(seconds) {
  const rounded = roundDuration(seconds);
  if (rounded <= 0) return '0m';
  const h = Math.floor(rounded / 3600);
  const m = Math.floor((rounded % 3600) / 60);
  if (h <= 0) return `${m}m`;
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
}

/** Decimal-hours view of a rounded duration ("1.25", "0.75", "2", "0"). */
function formatRoundedDecimalHours(seconds) {
  return secondsToDecimalHours(roundDuration(seconds));
}

/** True when entry is completed (never treat live timers as stopped). */
function isStoppedEntry(row) {
  if (!row) return false;
  const end = row.stopped_at;
  return end != null && String(end).trim() !== '';
}

/** Duration for CSV / stopped aggregates only — no live elapsed time. */
function computeStoppedEntrySeconds(row) {
  if (!row || !isStoppedEntry(row) || !row.started_at) return 0;
  if (
    row.duration_seconds != null &&
    Number.isFinite(row.duration_seconds) &&
    row.duration_seconds >= 0
  ) {
    return Math.floor(row.duration_seconds);
  }
  return Math.max(
    0,
    Math.floor((new Date(row.stopped_at) - new Date(row.started_at)) / 1000),
  );
}

/** Decimal minutes from elapsed seconds (e.g. 90 → "1.5"). */
function formatDurationMinutesDecimal(seconds) {
  if (seconds == null || !Number.isFinite(seconds)) return '0';
  const mins = Number(seconds) / 60;
  return mins % 1 === 0 ? String(Math.round(mins)) : mins.toFixed(1);
}

/** Live-or-stopped raw seconds helper used across columns, CSV, and sorting. */
function computeEntrySeconds(row) {
  if (!row) return 0;
  if (!row.stopped_at && row.started_at) {
    return Math.max(
      0,
      Math.floor((Date.now() - new Date(row.started_at)) / 1000),
    );
  }
  if (row.duration_seconds != null && Number.isFinite(row.duration_seconds)) {
    return Math.max(0, Math.floor(row.duration_seconds));
  }
  if (row.started_at && row.stopped_at) {
    return Math.max(
      0,
      Math.floor((new Date(row.stopped_at) - new Date(row.started_at)) / 1000),
    );
  }
  return 0;
}

/** Format duration for display - shows actual minutes/hours (no rounding). */
function formatDuration(seconds) {
  if (seconds == null || !Number.isFinite(seconds)) return '0m';
  const s = Math.floor(Number(seconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (h >= 1) return `${h}h ${m}m`;
  return `${m}m`;
}

function formatHours(seconds) {
  if (seconds == null) return '0';
  return (seconds / 3600).toFixed(1);
}

/** HH:MM:SS for team week totals */
function formatDurationHMS(seconds) {
  if (seconds == null || !Number.isFinite(seconds)) return '00:00:00';
  const s = Math.max(0, Math.floor(Number(seconds)));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const x = s % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(
    x,
  ).padStart(2, '0')}`;
}

function getUserDisplay(user) {
  if (!user) return '—';
  return (
    user.display_name ||
    [user.first_name, user.last_name].filter(Boolean).join(' ') ||
    user.email ||
    '—'
  );
}

const REPORT_NAME_FORMATS = [
  { value: 'full', label: 'Full name' },
  { value: 'first', label: 'First name only' },
  { value: 'initial', label: 'First name + last initial' },
];
const REPORT_NAME_FORMAT_KEY = 'timeTrackerReportNameFormat';

function loadReportNameFormat() {
  try {
    const saved = window.localStorage.getItem(REPORT_NAME_FORMAT_KEY);
    if (saved === 'full' || saved === 'first' || saved === 'initial') {
      return saved;
    }
  } catch (e) {
    /* ignore private-mode storage failures */
  }
  return 'full';
}

function userNameParts(user) {
  let first = String(user?.first_name || '').trim();
  let last = String(user?.last_name || '').trim();
  if (!first && user?.display_name) {
    const bits = String(user.display_name)
      .trim()
      .split(/\s+/)
      .filter(Boolean);
    first = bits[0] || '';
    if (!last) last = bits.slice(1).join(' ');
  }
  return { first, last };
}

/** How the person who entered time appears on reports and in CSV export. */
function formatReportUserName(user, mode) {
  if (!user) return '—';
  const full = getUserDisplay(user);
  if (mode !== 'first' && mode !== 'initial') return full;
  const { first, last } = userNameParts(user);
  if (!first) return full;
  if (mode === 'first') return first;
  if (!last) return first;
  return `${first} ${last.charAt(0).toUpperCase()}.`;
}

function reportUserInitials(user, mode) {
  const { first, last } = userNameParts(user);
  if (mode === 'first') {
    return (
      first.charAt(0) ||
      user?.display_name?.charAt(0) ||
      user?.email?.charAt(0) ||
      '?'
    ).toUpperCase();
  }
  const letters = `${first.charAt(0) || ''}${last.charAt(0) || ''}`;
  if (letters) return letters.toUpperCase();
  return (
    user?.display_name?.charAt(0) ||
    user?.email?.charAt(0) ||
    '?'
  ).toUpperCase();
}

/** Ant Design Select filter: `label` may be a React node (e.g. project color dot). Prefer `searchLabel` when present. */
function selectSearchFilterOption(input, option) {
  const q = String(input || '').toLowerCase();
  const raw = option?.searchLabel ?? option?.label ?? '';
  const text = typeof raw === 'string' ? raw : '';
  return text.toLowerCase().includes(q);
}

function getProjectDisplay(project) {
  if (!project) return 'Unnamed project';
  const name =
    project.project_name || (typeof project === 'string' ? project : null);
  return name && name.trim() ? name.trim() : 'Unnamed project';
}

/** Sort report rows by start time (newest first) so edits to start time reorder the list. */
function compareReportEntriesDesc(a, b) {
  const ta = new Date(a.started_at || 0).getTime();
  const tb = new Date(b.started_at || 0).getTime();
  if (tb !== ta) return tb - ta;
  return String(b._id || '').localeCompare(String(a._id || ''));
}

export default function TimeTrackerReportsPage() {
  const history = useHistory();
  const isPayrollAdmin = Number(Auth.getRole()) === 10;
  const [permissions, setPermissions] = useState(null);
  const [projects, setProjects] = useState([]);
  const [users, setUsers] = useState([]);
  const [summary, setSummary] = useState(null);
  const [entries, setEntries] = useState([]);
  const [total, setTotal] = useState(0);
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [loadingEntries, setLoadingEntries] = useState(false);
  const [teamActivity, setTeamActivity] = useState([]);
  const [loadingTeamActivity, setLoadingTeamActivity] = useState(false);
  const [liveTick, setLiveTick] = useState(0);
  // Multi-select: lists of project ids / user ids that are currently filtered.
  // Empty array means "all allowed" (no filter).
  const [projectIds, setProjectIds] = useState([]);
  const [userIds, setUserIds] = useState([]);
  const [nameFormat, setNameFormat] = useState(loadReportNameFormat);
  const [datePreset, setDatePreset] = useState('thisWeek');
  const [customRange, setCustomRange] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [editingEntry, setEditingEntry] = useState(null);
  const [editSaving, setEditSaving] = useState(false);
  const [editForm] = Form.useForm();
  const [payroll, setPayroll] = useState(null);
  const [loadingPayroll, setLoadingPayroll] = useState(false);
  const [stoppingId, setStoppingId] = useState(null);

  // Use the same persisted tracker timezone as the tracker page so "this week"
  // / "last week" always mean Monday 00:01 in the user's local zone.
  const effectiveTz = useMemo(
    () => getEffectiveTimezone(loadStoredTimezone()),
    [],
  );

  const dateRange = useMemo(
    () => getDateRange(datePreset, customRange, effectiveTz),
    [datePreset, customRange, effectiveTz],
  );

  const queryParams = useMemo(() => {
    const q = { page, limit: pageSize };
    if (projectIds.length > 0) q.project = projectIds;
    if (userIds.length > 0) q.user = userIds;
    if (dateRange.from) q.dateFrom = dateRange.from;
    if (dateRange.to) q.dateTo = dateRange.to;
    return q;
  }, [page, pageSize, projectIds, userIds, dateRange]);

  const loadSummary = useCallback(() => {
    const params = {};
    if (projectIds.length > 0) params.project = projectIds;
    if (userIds.length > 0) params.user = userIds;
    if (dateRange.from) params.dateFrom = dateRange.from;
    if (dateRange.to) params.dateTo = dateRange.to;
    setLoadingSummary(true);
    timeTrackerReportsSummaryService(params)
      .then((res) => {
        setSummary(res.data);
        setLoadingSummary(false);
      })
      .catch((err) => {
        handleApiError(err);
        setLoadingSummary(false);
      });
  }, [projectIds, userIds, dateRange]);

  const loadEntries = useCallback(() => {
    setLoadingEntries(true);
    timeTrackerReportsEntriesService(queryParams)
      .then((res) => {
        setEntries(res.data?.items || []);
        setTotal(res.data?.total ?? 0);
        setLoadingEntries(false);
      })
      .catch((err) => {
        handleApiError(err);
        setEntries([]);
        setTotal(0);
        setLoadingEntries(false);
      });
  }, [queryParams]);

  const loadPayroll = useCallback(() => {
    if (!isPayrollAdmin) {
      setPayroll(null);
      setLoadingPayroll(false);
      return;
    }
    const params = { timezone: effectiveTz };
    if (userIds.length > 0) params.user = userIds;
    if (dateRange.from) params.dateFrom = dateRange.from;
    if (dateRange.to) params.dateTo = dateRange.to;
    setLoadingPayroll(true);
    timeTrackerReportsPayrollService(params)
      .then((res) => {
        setPayroll(res.data);
        setLoadingPayroll(false);
      })
      .catch((err) => {
        handleApiError(err);
        setPayroll(null);
        setLoadingPayroll(false);
      });
  }, [userIds, dateRange, effectiveTz, isPayrollAdmin]);

  const loadTeamActivity = useCallback(() => {
    setLoadingTeamActivity(true);
    const params = {};
    if (projectIds.length > 0) params.project = projectIds;
    if (userIds.length > 0) params.user = userIds;
    if (dateRange.from) params.dateFrom = dateRange.from;
    if (dateRange.to) params.dateTo = dateRange.to;
    timeTrackerReportsTeamActivityService(params)
      .then((res) => {
        setTeamActivity(res.data?.items || []);
        setLoadingTeamActivity(false);
      })
      .catch((err) => {
        handleApiError(err);
        setTeamActivity([]);
        setLoadingTeamActivity(false);
      });
  }, [projectIds, userIds, dateRange]);

  useEffect(() => {
    accessControls
      .fetch()
      .then(setPermissions)
      .catch(() => setPermissions({}));
  }, []);

  useEffect(() => {
    if (!accessControls.can(permissions, 'TimeTracker.read:any')) return;
    timeTrackerProjectsListService()
      .then((res) => setProjects(res.data?.items || []))
      .catch(() => setProjects([]));
    usersListService()
      .then((res) => {
        const raw = res.data?.items || res.data || [];
        const list = Array.isArray(raw) ? [...raw] : [];

        // When a super admin (or any user whose home client differs from the
        // active reseller context) views these reports, the `/users`
        // data-guard will filter them out of the dropdown. Fetch their own
        // record via `/users/verify-token` — which skips that data-guard —
        // and append it, so they can always filter on their own entries.
        const payload = Auth.getPayload ? Auth.getPayload() : null;
        const selfId = payload && payload.userId ? String(payload.userId) : '';
        const alreadyPresent = list.some((u) => String(u._id) === selfId);

        if (selfId && !alreadyPresent) {
          usersVerifyTokenService()
            .then((selfRes) => {
              const selfItem =
                (selfRes.data && (selfRes.data.item || selfRes.data)) || null;
              if (selfItem && selfItem._id) {
                setUsers([...list, selfItem]);
              } else {
                setUsers(list);
              }
            })
            .catch(() => setUsers(list));
        } else {
          setUsers(list);
        }
      })
      .catch(() => setUsers([]));
  }, [permissions]);

  useEffect(() => {
    if (!accessControls.can(permissions, 'TimeTracker.read:any')) return;
    if (datePreset === 'custom' && !customRange?.length) return;
    loadSummary();
  }, [
    permissions,
    projectIds,
    userIds,
    dateRange,
    datePreset,
    customRange,
    loadSummary,
  ]);

  useEffect(() => {
    if (!accessControls.can(permissions, 'TimeTracker.read:any')) return;
    if (datePreset === 'custom' && !customRange?.length) return;
    loadEntries();
  }, [permissions, queryParams, datePreset, customRange, loadEntries]);

  useEffect(() => {
    if (!accessControls.can(permissions, 'TimeTracker.read:any')) return;
    if (datePreset === 'custom' && !customRange?.length) return;
    loadTeamActivity();
  }, [
    permissions,
    projectIds,
    userIds,
    datePreset,
    customRange,
    loadTeamActivity,
  ]);

  useEffect(() => {
    if (!isPayrollAdmin) return;
    if (!accessControls.can(permissions, 'TimeTracker.read:any')) return;
    if (datePreset === 'custom' && !customRange?.length) return;
    loadPayroll();
  }, [permissions, userIds, datePreset, customRange, loadPayroll, isPayrollAdmin]);

  useEffect(() => {
    const hasRunning =
      teamActivity.some((r) => r.inProgress) ||
      entries.some((e) => !e.stopped_at);
    if (!hasRunning) return undefined;
    const id = setInterval(() => setLiveTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, [teamActivity, entries]);

  const projectOptions = useMemo(
    () =>
      [...projects]
        .sort((a, b) =>
          (a.project_name || '').localeCompare(b.project_name || ''),
        )
        .map((p) => {
          const searchLabel = p.project_name || String(p._id);
          return {
            value: p._id,
            searchLabel,
            label: (
              <span
                style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}
              >
                <span
                  style={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    flexShrink: 0,
                    background: getProjectColor(p),
                  }}
                />
                <span>{searchLabel}</span>
              </span>
            ),
          };
        }),
    [projects],
  );

  const displayUser = useCallback(
    (user) => formatReportUserName(user, nameFormat),
    [nameFormat],
  );

  useEffect(() => {
    try {
      window.localStorage.setItem(REPORT_NAME_FORMAT_KEY, nameFormat);
    } catch (e) {
      /* ignore private-mode storage failures */
    }
  }, [nameFormat]);

  const userOptions = useMemo(
    () =>
      [...users]
        .sort((a, b) => displayUser(a).localeCompare(displayUser(b)))
        .map((u) => {
          const searchLabel = displayUser(u);
          return {
            value: u._id,
            searchLabel,
            label: searchLabel,
          };
        }),
    [users, displayUser],
  );

  const buildCsvRows = (list) =>
    (list || [])
      .filter((e) => isStoppedEntry(e))
      .map((e) => {
        const rawSec = computeStoppedEntrySeconds(e);
        return [
          (e.task_description || '').replace(/"/g, '""'),
          (e.project?.project_name || '').replace(/"/g, '""'),
          displayUser(e.user).replace(/"/g, '""'),
          e.started_at
            ? moment.tz(e.started_at, effectiveTz).format('YYYY-MM-DD HH:mm')
            : '',
          moment.tz(e.stopped_at, effectiveTz).format('YYYY-MM-DD HH:mm'),
          formatDurationMinutesDecimal(rawSec),
          secondsToDecimalHours(rawSec),
          formatRoundedDuration(rawSec),
          formatRoundedDecimalHours(rawSec),
        ];
      });

  const exportCsv = (exportAll = false) => {
    const headers = [
      'Task',
      'Project',
      'User',
      'Start',
      'End',
      'Duration (minutes)',
      'Duration (hours)',
      'Duration (rounded)',
      'Rounded hours (decimal)',
    ];
    if (exportAll && total > entries.length) {
      const params = { limit: Math.min(total, 5000), page: 1 };
      if (projectIds.length > 0) params.project = projectIds;
      if (userIds.length > 0) params.user = userIds;
      if (dateRange.from) params.dateFrom = dateRange.from;
      if (dateRange.to) params.dateTo = dateRange.to;
      const hide = message.loading('Preparing export...', 0);
      timeTrackerReportsEntriesService(params)
        .then((res) => {
          const list = [...(res.data?.items || [])].sort(
            compareReportEntriesDesc,
          );
          const rows = buildCsvRows(list);
          const csv = [
            headers.map((h) => `"${h}"`).join(','),
            ...rows.map((r) => r.map((c) => `"${c}"`).join(',')),
          ].join('\n');
          const blob = new Blob(['\uFEFF' + csv], {
            type: 'text/csv;charset=utf-8',
          });
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `time-tracker-report-${moment().format(
            'YYYY-MM-DD',
          )}.csv`;
          a.click();
          URL.revokeObjectURL(url);
          hide();
          const omitted = list.length - rows.length;
          message.success(
            omitted > 0
              ? `Exported ${rows.length} stopped entries (${omitted} running timer${omitted === 1 ? '' : 's'} omitted)`
              : `Exported ${rows.length} stopped entries`,
          );
        })
        .catch(() => {
          hide();
          message.error('Export failed');
        });
      return;
    }
    const list = [...entries].sort(compareReportEntriesDesc);
    const rows = buildCsvRows(list);
    const csv = [
      headers.map((h) => `"${h}"`).join(','),
      ...rows.map((r) => r.map((c) => `"${c}"`).join(',')),
    ].join('\n');
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `time-tracker-report-${moment().format('YYYY-MM-DD')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    const omitted = list.length - rows.length;
    message.success(
      omitted > 0
        ? `Exported ${rows.length} stopped entries on this page (${omitted} running omitted)`
        : `Exported ${rows.length} stopped entries on this page`,
    );
  };

  const clearFilters = () => {
    setProjectIds([]);
    setUserIds([]);
    setDatePreset('thisWeek');
    setCustomRange(null);
    setPage(1);
  };

  const hasActiveFilters =
    projectIds.length > 0 ||
    userIds.length > 0 ||
    datePreset !== 'thisWeek' ||
    (datePreset === 'custom' && customRange?.length);

  const dateRangeLabel = useMemo(() => {
    if (datePreset === 'custom' && customRange?.[0] && customRange?.[1]) {
      return `${customRange[0].format('MMM D')} – ${customRange[1].format(
        'MMM D, YYYY',
      )}`;
    }
    if (dateRange.from && dateRange.to) {
      const fromM = moment.tz(dateRange.from, effectiveTz);
      const toM = moment.tz(dateRange.to, effectiveTz);
      if (datePreset === 'thisWeek' || datePreset === 'lastWeek') {
        return `${fromM.format('ddd MMM D')} – ${toM.format(
          'ddd MMM D, YYYY',
        )}`;
      }
      return `${fromM.format('MMM D')} – ${toM.format('MMM D, YYYY')}`;
    }
    return null;
  }, [datePreset, customRange, dateRange, effectiveTz]);

  const openEditEntry = useCallback(
    (row) => {
      if (!row?.stopped_at) {
        message.info('Stop the running timer before editing this entry.');
        return;
      }
      setEditingEntry(row);
      editForm.setFieldsValue({
        task_description: row.task_description || '',
        started_at: moment.tz(row.started_at, effectiveTz),
        stopped_at: moment.tz(row.stopped_at, effectiveTz),
      });
    },
    [editForm, effectiveTz],
  );

  const closeEditEntry = useCallback(() => {
    setEditingEntry(null);
    editForm.resetFields();
  }, [editForm]);

  const forceStopEntry = useCallback(
    (row) => {
      if (!row?._id) return;
      setStoppingId(row._id);
      timeTrackerEntryStopService(row._id, {
        task_description: row.task_description || '',
      })
        .then((res) => {
          handleApiSuccess(res);
          loadEntries();
          loadSummary();
          loadTeamActivity();
          loadPayroll();
        })
        .catch(handleApiError)
        .finally(() => setStoppingId(null));
    },
    [loadEntries, loadSummary, loadTeamActivity, loadPayroll],
  );

  const saveEditEntry = useCallback(() => {
    if (!editingEntry) return;
    editForm
      .validateFields()
      .then((values) => {
        const started = values.started_at;
        const stopped = values.stopped_at;
        if (!started || !stopped || !started.isValid() || !stopped.isValid()) {
          message.error('Start and end time are required');
          return;
        }
        if (!stopped.isAfter(started)) {
          message.warning('End time must be after start time');
          return;
        }
        setEditSaving(true);
        timeTrackerEntryUpdateService(editingEntry._id, {
          task_description: (values.task_description || '').trim(),
          started_at: started.toISOString(),
          stopped_at: stopped.toISOString(),
        })
          .then((res) => {
            handleApiSuccess(res);
            closeEditEntry();
            loadEntries();
            loadSummary();
            loadTeamActivity();
          })
          .catch(handleApiError)
          .finally(() => setEditSaving(false));
      })
      .catch(() => {});
  }, [
    editingEntry,
    editForm,
    closeEditEntry,
    loadEntries,
    loadSummary,
    loadTeamActivity,
  ]);

  const columns = useMemo(
    () => [
      {
        title: 'Task',
        dataIndex: 'task_description',
        key: 'task_description',
        ellipsis: true,
        render: (t) => t || '—',
      },
      {
        title: 'Project',
        key: 'project',
        render: (_, r) => (
          <span
            style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}
          >
            <span
              style={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                flexShrink: 0,
                background: r.project
                  ? getProjectColor(r.project)
                  : getNoProjectColor(),
              }}
            />
            <span>
              {r.project?.project_name || (r.stopped_at ? '—' : '(no project)')}
            </span>
          </span>
        ),
      },
      {
        title: 'User',
        key: 'user',
        render: (_, r) => displayUser(r.user),
      },
      {
        title: 'Start',
        dataIndex: 'started_at',
        key: 'started_at',
        width: 160,
        sorter: (a, b) => new Date(a.started_at) - new Date(b.started_at),
        render: (t) =>
          t ? moment.tz(t, effectiveTz).format('MMM D, YYYY HH:mm') : '—',
      },
      {
        title: 'End',
        dataIndex: 'stopped_at',
        key: 'stopped_at',
        width: 180,
        render: (t) =>
          t ? (
            moment.tz(t, effectiveTz).format('MMM D, YYYY HH:mm')
          ) : (
            <span className="reports-running-end">
              <Tag color="red">Running</Tag>
            </span>
          ),
      },
      {
        title: 'Duration',
        key: 'duration',
        width: 120,
        sorter: (a, b) => computeEntrySeconds(a) - computeEntrySeconds(b),
        render: (_, r) => {
          void liveTick;
          const sec = computeEntrySeconds(r);
          return !r.stopped_at && r.started_at
            ? formatDurationHMS(sec)
            : formatDuration(sec);
        },
      },
      {
        title: (
          <Tooltip title="From stored start/stop span for finished entries; for running rows, elapsed time so far.">
            Duration (minutes)
          </Tooltip>
        ),
        key: 'durationMinutes',
        width: 128,
        align: 'right',
        sorter: (a, b) => computeEntrySeconds(a) - computeEntrySeconds(b),
        render: (_, r) => {
          void liveTick;
          return formatDurationMinutesDecimal(computeEntrySeconds(r));
        },
      },
      {
        title: (
          <Tooltip title="Duration snapped to the nearest 15 minutes (round down until the 7m 30s midpoint, then round up).">
            <span>Rounded</span>
          </Tooltip>
        ),
        key: 'durationRounded',
        width: 120,
        sorter: (a, b) =>
          roundDuration(computeEntrySeconds(a)) -
          roundDuration(computeEntrySeconds(b)),
        render: (_, r) => {
          void liveTick;
          return formatRoundedDuration(computeEntrySeconds(r));
        },
      },
      {
        title: (
          <Tooltip title="Rounded duration in decimal hours (15 min = 0.25, 30 min = 0.5, 45 min = 0.75, 1h = 1).">
            <span>Hours (decimal)</span>
          </Tooltip>
        ),
        key: 'durationDecimal',
        width: 130,
        sorter: (a, b) =>
          roundDuration(computeEntrySeconds(a)) -
          roundDuration(computeEntrySeconds(b)),
        render: (_, r) => {
          void liveTick;
          return formatRoundedDecimalHours(computeEntrySeconds(r));
        },
      },
      {
        title: '',
        key: 'actions',
        width: 72,
        fixed: 'right',
        render: (_, r) =>
          r.stopped_at ? (
            <Tooltip title="Edit time">
              <Button
                type="link"
                size="small"
                icon={<EditOutlined />}
                onClick={() => openEditEntry(r)}
                aria-label="Edit time entry"
              />
            </Tooltip>
          ) : (
            <Popconfirm
              title={`Stop ${displayUser(r.user)}'s running timer?`}
              okText="Stop clock"
              okButtonProps={{ danger: true }}
              onConfirm={() => forceStopEntry(r)}
            >
              <Tooltip title="Force-stop this user's clock">
                <Button
                  type="link"
                  size="small"
                  danger
                  icon={<StopOutlined />}
                  loading={stoppingId === r._id}
                  aria-label="Force stop timer"
                />
              </Tooltip>
            </Popconfirm>
          ),
      },
    ],
    [
      liveTick,
      effectiveTz,
      openEditEntry,
      forceStopEntry,
      stoppingId,
      displayUser,
    ],
  );

  /** API order may not reflect `started_at` after edits; keep rows chronological on the page. */
  const sortedEntries = useMemo(
    () => [...entries].sort(compareReportEntriesDesc),
    [entries],
  );

  const teamActivityColumns = useMemo(
    () => [
      {
        title: 'Team member',
        key: 'member',
        width: 220,
        sorter: (a, b) =>
          displayUser(a.user).localeCompare(displayUser(b.user), undefined, {
            sensitivity: 'base',
          }),
        render: (_, row) => {
          const name = displayUser(row.user);
          const initial = reportUserInitials(row.user, nameFormat);
          return (
            <div className="reports-team-member">
              <Avatar src={row.user?.photo} size={40}>
                {initial.slice(0, 2).toUpperCase()}
              </Avatar>
              <span className="reports-team-member-name" title={name}>
                {name}
              </span>
            </div>
          );
        },
      },
      {
        title: 'Latest activity',
        key: 'latest',
        render: (_, row) => {
          const ent = row.latestEntry;
          const taskRaw = ent?.task_description;
          const task =
            taskRaw && String(taskRaw).trim()
              ? String(taskRaw).trim()
              : '(no description)';
          const projName = ent?.project?.project_name || '(Without project)';
          const dotColor = ent?.project
            ? getProjectColor(ent.project)
            : getNoProjectColor();
          return (
            <div className="reports-team-latest">
              <div className="reports-team-latest-main">
                <Tooltip title={task.length > 64 ? task : undefined}>
                  <div className="reports-team-latest-task">{task}</div>
                </Tooltip>
                <div className="reports-team-latest-project">
                  <span
                    className="reports-team-latest-project-dot"
                    style={{ background: dotColor }}
                  />
                  <Tooltip title={projName.length > 40 ? projName : undefined}>
                    <span>{projName}</span>
                  </Tooltip>
                </div>
              </div>
              <div className="reports-team-latest-meta">
                <div className="reports-team-latest-duration">
                  {formatDurationHMS(row.latestActivitySeconds || 0)}
                </div>
                <div className="reports-team-latest-status">
                  {row.inProgress ? (
                    <span className="reports-team-in-progress">
                      <span className="reports-team-in-progress-dot" />
                      In progress
                    </span>
                  ) : row.latestStoppedAt ? (
                    moment(row.latestStoppedAt).fromNow()
                  ) : (
                    '—'
                  )}
                </div>
              </div>
            </div>
          );
        },
      },
      {
        title: (
          <Tooltip
            title={
              dateRangeLabel
                ? `Completed (stopped) time only — entries that started in the selected period (${dateRangeLabel}). Same basis as the summary widgets. In-progress timers are not included.`
                : 'Completed (stopped) time only in the selected date range (same basis as the summary widgets). In-progress timers are not included.'
            }
          >
            <span>Total time</span>
          </Tooltip>
        ),
        key: 'week',
        width: 280,
        sorter: (a, b) => (a.weekTotalSeconds || 0) - (b.weekTotalSeconds || 0),
        render: (_, row) => {
          const total = row.weekTotalSeconds || 0;
          const parts = row.weekByProject || [];
          return (
            <div className="reports-team-week-cell">
              <span className="reports-team-week-time">
                {formatDurationHMS(total)}
              </span>
              <div
                className="reports-team-activity-bar"
                title="Stopped time by project in the selected period"
              >
                {total <= 0
                  ? null
                  : parts.map((seg, i) => {
                      const pid = seg.project?._id;
                      const title = `${
                        seg.project?.project_name || '(Without project)'
                      } — ${formatDuration(seg.seconds)}`;
                      return (
                        <Tooltip
                          key={`${row.user?._id || 'u'}-${pid || 'np'}-${i}`}
                          title={title}
                        >
                          <div
                            className="reports-team-activity-bar-seg"
                            style={{
                              flex: seg.seconds,
                              flexBasis: 0,
                              minWidth: 4,
                              background: seg.project
                                ? getProjectColor(seg.project)
                                : getNoProjectColor(),
                            }}
                          />
                        </Tooltip>
                      );
                    })}
              </div>
            </div>
          );
        },
      },
    ],
    [dateRangeLabel, displayUser, nameFormat],
  );

  const teamActivityRollup = useMemo(
    () => computeTeamActivityRollup(teamActivity),
    [teamActivity],
  );

  if (permissions == null) {
    return (
      <div style={{ padding: 24, textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }
  if (!accessControls.can(permissions, 'TimeTracker.read:any')) {
    return (
      <div style={{ padding: 24 }}>
        <Result
          status="403"
          title="Access denied"
          subTitle="You do not have permission to view Time Tracker Reports."
          extra={
            <Button type="primary" onClick={() => history.push('/')}>
              Go to Dashboard
            </Button>
          }
        />
      </div>
    );
  }

  const topContributors = summary?.topContributors || [];
  const topProjects = summary?.topProjects || [];
  const topTasks = summary?.topTasks || [];

  const getTaskDisplay = (taskDescription) => {
    if (taskDescription == null || taskDescription === '')
      return 'No description';
    const s = String(taskDescription).trim();
    return s || 'No description';
  };

  return (
    <div className="time-tracker-reports">
      {/* Filters */}
      <Card size="small" className="reports-filters">
        <Row
          gutter={[16, 12]}
          align="middle"
          justify="space-between"
          style={{ marginBottom: 16 }}
        >
          <Col>
            <Typography.Title level={4} style={{ margin: 0 }}>
              Time Tracker Reports
            </Typography.Title>
          </Col>
          <Col>
            <Space wrap>
              <Tooltip title="Reload data">
                <Button
                  icon={<ReloadOutlined />}
                  onClick={() => {
                    loadSummary();
                    loadEntries();
                    loadTeamActivity();
                    loadPayroll();
                  }}
                >
                  Refresh
                </Button>
              </Tooltip>
              {hasActiveFilters && (
                <Button icon={<ClearOutlined />} onClick={clearFilters}>
                  Clear filters
                </Button>
              )}
              <Tooltip title="Exports completed (stopped) entries only — running timers are never included so open timers do not add hours to the CSV. Current page rows only.">
                <Button
                  type="primary"
                  icon={<DownloadOutlined />}
                  onClick={() => exportCsv(false)}
                >
                  Export CSV
                </Button>
              </Tooltip>
              {total > pageSize && (
                <Tooltip
                  title={`Export up to ${Math.min(
                    total,
                    5000,
                  )} rows matching filters. Stopped entries only — running timers omitted.`}
                >
                  <Button
                    icon={<DownloadOutlined />}
                    onClick={() => exportCsv(true)}
                  >
                    Export all
                  </Button>
                </Tooltip>
              )}
            </Space>
          </Col>
        </Row>
        <Row gutter={[16, 12]} align="middle">
          <Col xs={24} sm={12} md={5}>
            <Text type="secondary">Projects</Text>
            <Select
              mode="multiple"
              placeholder="All projects"
              allowClear
              showSearch
              maxTagCount="responsive"
              filterOption={selectSearchFilterOption}
              optionFilterProp="searchLabel"
              options={projectOptions}
              value={projectIds}
              onChange={(v) => {
                setProjectIds(Array.isArray(v) ? v : []);
                setPage(1);
              }}
              style={{ width: '100%', marginTop: 4 }}
            />
          </Col>
          <Col xs={24} sm={12} md={5}>
            <Text type="secondary">Users</Text>
            <Select
              mode="multiple"
              placeholder="All users"
              allowClear
              showSearch
              maxTagCount="responsive"
              filterOption={selectSearchFilterOption}
              optionFilterProp="searchLabel"
              options={userOptions}
              value={userIds}
              onChange={(v) => {
                setUserIds(Array.isArray(v) ? v : []);
                setPage(1);
              }}
              style={{ width: '100%', marginTop: 4 }}
            />
          </Col>
          <Col xs={24} sm={12} md={5}>
            <Tooltip title="Controls how the person who entered the time is shown on this report and in the CSV export.">
              <Text type="secondary">Names</Text>
            </Tooltip>
            <Select
              value={nameFormat}
              onChange={setNameFormat}
              options={REPORT_NAME_FORMATS}
              style={{ width: '100%', marginTop: 4 }}
            />
          </Col>
          <Col xs={24} sm={12} md={5}>
            <Text type="secondary">Date range</Text>
            <Select
              value={datePreset}
              onChange={setDatePreset}
              options={DATE_PRESETS}
              style={{ width: '100%', marginTop: 4 }}
            />
          </Col>
          {datePreset === 'custom' && (
            <Col xs={24} sm={12} md={5}>
              <Text type="secondary">Custom range</Text>
              <RangePicker
                value={customRange}
                onChange={setCustomRange}
                style={{ width: '100%', marginTop: 4 }}
              />
            </Col>
          )}
          {dateRangeLabel && (
            <Col xs={24} sm={12} md={4}>
              <Text type="secondary">Selected period</Text>
              <div className="reports-date-label">{dateRangeLabel}</div>
            </Col>
          )}
        </Row>
      </Card>

      <Tabs className="reports-main-tabs" animated={false}>
        <TabPane tab="Report" key="report">
          {/* Widgets */}
          {loadingSummary ? (
            <div className="reports-widgets-loading">
              <Spin />
            </div>
          ) : (
            <Row gutter={[16, 16]} className="reports-widgets">
              <Col xs={24} sm={12} lg={6}>
                <Card className="report-widget report-widget-total">
                  <Statistic
                    title="Total time"
                    value={formatHours(summary?.totalHours ?? 0)}
                    suffix="h"
                    prefix={<ClockCircleOutlined />}
                  />
                  <div className="report-widget-total-metrics">
                    <Tooltip title="Actual elapsed time (HH:MM:SS) in the selected period.">
                      <div className="report-widget-total-metric">
                        <Text type="secondary">Actual (HMS)</Text>
                        <div className="report-widget-total-metric-value">
                          {formatDurationHMS(summary?.totalHours ?? 0)}
                        </div>
                      </div>
                    </Tooltip>
                    <Tooltip title="Total duration expressed as decimal minutes (e.g. 90s → 1.5).">
                      <div className="report-widget-total-metric">
                        <Text type="secondary">Per-minute</Text>
                        <div className="report-widget-total-metric-value">
                          {formatDurationMinutesDecimal(
                            summary?.totalHours ?? 0,
                          )}
                          <span className="report-widget-total-metric-unit">
                            {' '}
                            min
                          </span>
                        </div>
                      </div>
                    </Tooltip>
                    <Tooltip title="Each entry snapped to the nearest 15 minutes (half-up at 7m 30s), then summed. Matches the per-row rounded values in CSV exports.">
                      <div className="report-widget-total-metric">
                        <Text type="secondary">Rounded</Text>
                        <div className="report-widget-total-metric-value">
                          {formatDuration(
                            summary?.totalRoundedSeconds ??
                              roundDuration(summary?.totalHours ?? 0),
                          )}
                        </div>
                      </div>
                    </Tooltip>
                    <Tooltip title="Per-entry rounded total expressed as decimal hours (15 min = .25, 30 min = .5, 1 hr = 1). Matches the per-row decimal hours in CSV exports.">
                      <div className="report-widget-total-metric">
                        <Text type="secondary">Decimal (rounded)</Text>
                        <div className="report-widget-total-metric-value">
                          {secondsToDecimalHours(
                            summary?.totalRoundedSeconds ??
                              roundDuration(summary?.totalHours ?? 0),
                          )}
                          <span className="report-widget-total-metric-unit">
                            {' '}
                            h
                          </span>
                        </div>
                      </div>
                    </Tooltip>
                  </div>
                  <Text type="secondary">in selected period</Text>
                </Card>
              </Col>
              <Col xs={24} sm={12} lg={6}>
                <Card
                  className="report-widget report-widget-tasks"
                  title={
                    <>
                      <UnorderedListOutlined /> Top tasks
                    </>
                  }
                >
                  {topTasks.length === 0 ? (
                    <div className="widget-empty">
                      No time logged in this period
                    </div>
                  ) : (
                    <ul className="widget-list-clean">
                      {topTasks.slice(0, 3).map((t, i) => (
                        <li key={i} className="widget-list-clean-item">
                          <span className="widget-list-clean-rank">{i + 1}</span>
                          <span
                            className="widget-list-clean-label"
                            title={getTaskDisplay(t.task_description)}
                          >
                            {getTaskDisplay(t.task_description)}
                          </span>
                          <span className="widget-list-clean-value">
                            {formatDuration(t.totalSeconds)}
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                </Card>
              </Col>
              <Col xs={24} md={12} lg={6}>
                <Card
                  className="report-widget report-widget-contributors"
                  title={
                    <>
                      <TrophyOutlined /> Top contributors
                    </>
                  }
                >
                  {topContributors.length === 0 ? (
                    <div className="widget-empty">
                      No time logged in this period
                    </div>
                  ) : (
                    <ul className="widget-list-clean">
                      {topContributors.slice(0, 3).map((c, i) => (
                        <li
                          key={c.user?._id || i}
                          className="widget-list-clean-item"
                        >
                          <span className="widget-list-clean-rank">{i + 1}</span>
                          <span
                            className="widget-list-clean-label"
                            title={displayUser(c.user)}
                          >
                            {displayUser(c.user)}
                          </span>
                          <span className="widget-list-clean-value">
                            {formatDuration(c.totalSeconds)}
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                </Card>
              </Col>
              <Col xs={24} md={12} lg={6}>
                <Card
                  className="report-widget report-widget-projects"
                  title={
                    <>
                      <BarChartOutlined /> Top projects
                    </>
                  }
                >
                  {topProjects.length === 0 ? (
                    <div className="widget-empty">
                      No time logged in this period
                    </div>
                  ) : (
                    <ul className="widget-list-clean">
                      {topProjects.slice(0, 3).map((p, i) => (
                        <li
                          key={p.project?._id || i}
                          className="widget-list-clean-item"
                        >
                          <span className="widget-list-clean-rank">{i + 1}</span>
                          <span
                            className="widget-list-clean-label"
                            title={getProjectDisplay(p.project)}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 8,
                            }}
                          >
                            <span
                              style={{
                                width: 8,
                                height: 8,
                                borderRadius: '50%',
                                flexShrink: 0,
                                background: getProjectColor(p.project),
                              }}
                            />
                            <span className="widget-list-clean-label-text">
                              {getProjectDisplay(p.project)}
                            </span>
                          </span>
                          <span className="widget-list-clean-value">
                            {formatDuration(p.totalSeconds)}
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                </Card>
              </Col>
            </Row>
          )}

          {/* Time entries */}
          <Card
            title={
              <>
                <FileTextOutlined /> Time entries
              </>
            }
            className="reports-table-card"
          >
            <Table
              rowKey="_id"
              loading={loadingEntries}
              dataSource={sortedEntries}
              columns={columns}
              rowClassName={() => 'reports-table-row'}
              pagination={{
                current: page,
                pageSize,
                total,
                showSizeChanger: true,
                showTotal: (t) => `${t} entries`,
                pageSizeOptions: ['10', '20', '50', '100'],
                onChange: (p, size) => {
                  setPage(p);
                  setPageSize(size || 20);
                },
              }}
              size="middle"
              locale={{
                emptyText: (
                  <div className="reports-table-empty">
                    <FileTextOutlined
                      style={{
                        fontSize: 48,
                        color: 'rgba(0,0,0,0.15)',
                        marginBottom: 8,
                      }}
                    />
                    <Text type="secondary">
                      No time entries for the selected filters.
                    </Text>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      Try changing the date range or filters above.
                    </Text>
                  </div>
                ),
              }}
            />
          </Card>
        </TabPane>

        <TabPane
          tab={
            <span>
              <TeamOutlined /> Team activity
            </span>
          }
          key="team"
        >
          {/* Team activity — scoped to filters above */}
          <Card
            title={
              <>
                <TeamOutlined /> Who is tracking
              </>
            }
            className="reports-team-activity-card"
            extra={
              dateRangeLabel ? (
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Uses the same project, user, and date filters as the Report
                  tab. Totals ({dateRangeLabel}) are stopped time only — aligned
                  with summary widgets. Members with a running timer are listed
                  first; “Latest activity” may include in-progress time.
                </Text>
              ) : (
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Uses filters above. Totals are stopped time only; members with
                  a running timer listed first.
                </Text>
              )
            }
          >
            <Table
              rowKey={(r) => String(r.user?._id || '')}
              loading={loadingTeamActivity}
              dataSource={teamActivity}
              columns={teamActivityColumns}
              pagination={false}
              size="middle"
              summary={() =>
                teamActivity.length > 0 ? (
                  <Table.Summary>
                    <Table.Summary.Row className="reports-team-activity-summary-row">
                      <Table.Summary.Cell index={0}>
                        <Text strong>Total</Text>
                        <div>
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            {teamActivity.length} member
                            {teamActivity.length === 1 ? '' : 's'} listed
                          </Text>
                        </div>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={1}>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          —
                        </Text>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={2}>
                        <div className="reports-team-footer-totals">
                          <div className="reports-team-footer-totals-metrics">
                            <div>
                              <Text type="secondary">Actual (HMS)</Text>
                              <div className="reports-team-footer-value">
                                {formatDurationHMS(teamActivityRollup.sumRaw)}
                              </div>
                            </div>
                            <div>
                              <Text type="secondary">Duration (minutes)</Text>
                              <div className="reports-team-footer-value">
                                {formatDurationMinutesDecimal(teamActivityRollup.sumRaw)}
                              </div>
                            </div>
                            <div>
                              <Text type="secondary">Duration (hours)</Text>
                              <div className="reports-team-footer-value">
                                {secondsToDecimalHours(teamActivityRollup.sumRaw)}
                              </div>
                            </div>
                            <div>
                              <Text type="secondary">Rounded sum</Text>
                              <Tooltip title="Each member total is rounded to 15 minutes first, then summed.">
                                <div className="reports-team-footer-value">
                                  {formatDuration(teamActivityRollup.sumRounded)}
                                </div>
                              </Tooltip>
                            </div>
                            <div>
                              <Text type="secondary">Rounded (decimal h)</Text>
                              <div className="reports-team-footer-value">
                                {secondsToDecimalHours(teamActivityRollup.sumRounded)}
                              </div>
                            </div>
                          </div>
                          <div
                            className="reports-team-activity-bar"
                            title="Stopped time by project (all listed members)"
                          >
                            {teamActivityRollup.sumRaw <= 0
                              ? null
                              : teamActivityRollup.weekByProject.map((seg, i) => {
                                  const pid = seg.project?._id;
                                  const title = `${
                                    seg.project?.project_name || '(Without project)'
                                  } — ${formatDuration(seg.seconds)}`;
                                  return (
                                    <Tooltip key={`footer-${pid || 'np'}-${i}`} title={title}>
                                      <div
                                        className="reports-team-activity-bar-seg"
                                        style={{
                                          flex: seg.seconds,
                                          flexBasis: 0,
                                          minWidth: 4,
                                          background: seg.project
                                            ? getProjectColor(seg.project)
                                            : getNoProjectColor(),
                                        }}
                                      />
                                    </Tooltip>
                                  );
                                })}
                          </div>
                        </div>
                      </Table.Summary.Cell>
                    </Table.Summary.Row>
                  </Table.Summary>
                ) : null
              }
              locale={{
                emptyText: (
                  <div style={{ padding: 16 }}>
                    <Text type="secondary">
                      {dateRange.from && dateRange.to
                        ? 'No team members matched this report for the filters above.'
                        : 'No team members with tracker activity in your access scope.'}
                    </Text>
                  </div>
                ),
              }}
            />
          </Card>
        </TabPane>

        {isPayrollAdmin && (
        <TabPane
          tab={
            <span>
              <DollarOutlined /> Payroll / activities
            </span>
          }
          key="payroll"
        >
          <Card
            title="Hours by activity and person"
            extra={
              <Text type="secondary" style={{ fontSize: 12 }}>
                Uses schedule activities when the timer was started from a
                shift. Cost uses Super Admin hourly rate (blank rate = $0).
              </Text>
            }
          >
            {loadingPayroll ? (
              <div style={{ padding: 32, textAlign: 'center' }}>
                <Spin />
              </div>
            ) : (
              <>
                <Row gutter={16} style={{ marginBottom: 16 }}>
                  <Col xs={24} sm={8}>
                    <Statistic
                      title="Total hours"
                      value={formatHours(payroll?.totals?.seconds || 0)}
                      suffix="h"
                    />
                  </Col>
                  <Col xs={24} sm={8}>
                    <Statistic
                      title="Total cost"
                      prefix="$"
                      value={(payroll?.totals?.cost || 0).toFixed(2)}
                    />
                  </Col>
                </Row>
                <Table
                  size="small"
                  rowKey={(r) =>
                    `${r.user?._id || ''}-${r.activity_id || r.activity_name}`
                  }
                  dataSource={payroll?.items || []}
                  pagination={false}
                  columns={[
                    {
                      title: 'Person',
                      key: 'user',
                      render: (_, r) => displayUser(r.user),
                    },
                    {
                      title: 'Activity / category',
                      dataIndex: 'activity_name',
                      key: 'activity',
                    },
                    {
                      title: 'Hours',
                      key: 'hours',
                      render: (_, r) => formatHours(r.seconds),
                    },
                    {
                      title: '$ / hr',
                      key: 'rate',
                      render: (_, r) =>
                        r.hourly_rate ? `$${Number(r.hourly_rate).toFixed(2)}` : '—',
                    },
                    {
                      title: 'Cost',
                      key: 'cost',
                      render: (_, r) => `$${(r.cost || 0).toFixed(2)}`,
                    },
                  ]}
                />
                <Typography.Title level={5} style={{ marginTop: 24 }}>
                  Person totals (time of day)
                </Typography.Title>
                <Table
                  size="small"
                  rowKey={(r) => String(r.user?._id || '')}
                  dataSource={payroll?.users || []}
                  pagination={false}
                  columns={[
                    {
                      title: 'Person',
                      key: 'user',
                      render: (_, r) => displayUser(r.user),
                    },
                    {
                      title: 'Hours',
                      key: 'hours',
                      render: (_, r) => formatHours(r.seconds),
                    },
                    {
                      title: 'Morning',
                      key: 'm',
                      render: (_, r) => formatHours(r.buckets?.morning || 0),
                    },
                    {
                      title: 'Afternoon',
                      key: 'a',
                      render: (_, r) => formatHours(r.buckets?.afternoon || 0),
                    },
                    {
                      title: 'Evening',
                      key: 'e',
                      render: (_, r) => formatHours(r.buckets?.evening || 0),
                    },
                    {
                      title: 'Cost',
                      key: 'cost',
                      render: (_, r) => `$${(r.cost || 0).toFixed(2)}`,
                    },
                  ]}
                />
              </>
            )}
          </Card>
        </TabPane>
        )}
      </Tabs>

      <Modal
        title={
          editingEntry
            ? `Edit time — ${displayUser(editingEntry.user)}`
            : 'Edit time'
        }
        visible={!!editingEntry}
        onCancel={closeEditEntry}
        onOk={saveEditEntry}
        okText="Save"
        confirmLoading={editSaving}
        destroyOnClose
        width={480}
      >
        <Form form={editForm} layout="vertical" requiredMark={false}>
          <Form.Item label="Task" name="task_description">
            <Input placeholder="What was worked on?" />
          </Form.Item>
          <Form.Item
            label="Start"
            name="started_at"
            rules={[{ required: true, message: 'Start time is required' }]}
          >
            <DatePicker
              showTime={{ format: 'HH:mm' }}
              format="MMM D, YYYY HH:mm"
              style={{ width: '100%' }}
            />
          </Form.Item>
          <Form.Item
            label="End"
            name="stopped_at"
            rules={[{ required: true, message: 'End time is required' }]}
          >
            <DatePicker
              showTime={{ format: 'HH:mm' }}
              format="MMM D, YYYY HH:mm"
              style={{ width: '100%' }}
            />
          </Form.Item>
          <Text type="secondary" style={{ fontSize: 12 }}>
            Times use your tracker timezone ({effectiveTz}). Duration updates
            from start and end.
          </Text>
        </Form>
      </Modal>
    </div>
  );
}
